<script>
export default {
    name: 'CollapsedSubmenuPanel'
};
</script>

<script setup>
import { computed, ref, watch } from 'vue';
import { LeftOutlined, RightOutlined } from '@ant-design/icons-vue';

const props = defineProps({
    nodes: {
        type: Array,
        default: () => []
    },
    rootTitle: {
        type: String,
        default: ''
    },
    active: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['item-click', 'title-change']);

const levelStack = ref([]);

const hasChildren = (node) =>
    Array.isArray(node.children) && node.children.length > 0;

const resetNavigation = () => {
    levelStack.value = [];
    emitTitleChange();
};

const emitTitleChange = () => {
    emit('title-change', currentTitle.value);
};

const currentNodes = computed(() => {
    if (levelStack.value.length === 0) {
        return props.nodes;
    }
    return levelStack.value[levelStack.value.length - 1].nodes;
});

const currentTitle = computed(() => {
    if (levelStack.value.length === 0) {
        return props.rootTitle;
    }
    return levelStack.value[levelStack.value.length - 1].title;
});

const canGoBack = computed(() => levelStack.value.length > 0);

const enterGroup = (node) => {
    if (!hasChildren(node)) {
        return;
    }
    levelStack.value.push({
        title: node.meta?.title || node.name,
        nodes: node.children
    });
    emitTitleChange();
};

const goBack = () => {
    if (levelStack.value.length === 0) {
        return;
    }
    levelStack.value.pop();
    emitTitleChange();
};

const handleItemClick = (routeName) => {
    if (!routeName) {
        return;
    }
    emit('item-click', routeName);
};

watch(
    () => props.active,
    (isActive) => {
        if (!isActive) {
            resetNavigation();
            emit('title-change', props.rootTitle);
        }
    }
);

watch(
    () => props.nodes,
    () => {
        if (!props.active) {
            resetNavigation();
        }
    },
    { deep: true }
);
</script>

<template>
    <div class="collapsed-submenu-panel">
        <button
            v-if="canGoBack"
            type="button"
            class="collapsed-submenu-back"
            @click.stop="goBack"
        >
            <LeftOutlined />
            <span>返回</span>
        </button>

        <template v-for="node in currentNodes" :key="node.name">
            <div
                v-if="hasChildren(node)"
                class="custom-submenu-item custom-submenu-item-group"
                @click.stop="enterGroup(node)"
            >
                <span>{{ node.meta?.title || node.name }}</span>
                <RightOutlined class="group-chevron" />
            </div>
            <div
                v-else
                class="custom-submenu-item"
                @click.stop="handleItemClick(node.name)"
            >
                {{ node.meta?.title || node.name }}
            </div>
        </template>
    </div>
</template>

<style scoped>
.collapsed-submenu-panel {
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-height: min(70vh, 420px);
    overflow-y: auto;
}

.collapsed-submenu-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    align-self: flex-start;
    margin: 0 8px 4px;
    padding: 4px 8px;
    border: none;
    background: transparent;
    color: #2a4a7a;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    border-radius: 6px;
    transition: background 0.2s ease;
}

.collapsed-submenu-back:hover {
    background: rgba(42, 74, 122, 0.12);
}

.custom-submenu-item {
    padding: 10px 16px;
    margin: 0 8px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    color: #4a4a5a;
    border: 1px solid transparent;
    background: rgba(255, 255, 255, 0.8);
    font-weight: 500;
}

.custom-submenu-item-group {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.group-chevron {
    color: #9b8afb;
    font-size: 12px;
    flex-shrink: 0;
}

.custom-submenu-item:hover {
    background: rgba(42, 74, 122, 0.15) !important;
    color: #2a2a3a !important;
    border-color: var(--neon-purple) !important;
    transform: translateX(4px);
    box-shadow: 0 0 12px rgba(42, 74, 122, 0.2);
}
</style>
